/**
 * Wattec — Gaming Performance chart.
 *
 * Draws an estimated frame rate for ten games at three resolutions from the
 * machine's processor, graphics card and memory.
 *
 * How the estimate is reached
 * ---------------------------
 * Two reference parts anchor everything: an RTX 5090 for graphics and a Ryzen
 * 7 9800X3D for the processor, both scored 100. Every other part is scored
 * against them as a single number, which for graphics cards tracks measured
 * frame rates closely enough to scale by directly.
 *
 * Each game then carries two things:
 *
 *   g — the frame rate the reference graphics card reaches at 1080p, 1440p
 *       and 2160p with an unlimited processor behind it, and
 *   c — the ceiling the reference processor puts on that game, which is the
 *       same at every resolution because the work the processor does does not
 *       grow with pixels.
 *
 * A machine's estimate is a soft minimum of the two, each scaled by its own
 * part's score (see BOTTLENECK). That is why a fast graphics card in a slow
 * machine stops gaining at 1080p but keeps gaining at 4K, which is how these
 * games really behave, and why Counter-Strike lands far above the rest on the
 * same hardware. A plain minimum was used until November 2026 and made the
 * processor count for nothing whenever the graphics card was the slower part
 * — the NOVA's 7500X3D and 7800X3D drew identical charts.
 *
 * The figures are estimates for Ultra-preset, native-resolution play with no
 * upscaling and no ray tracing, and the panel says so on the page. A product
 * whose parts cannot be identified shows no chart at all rather than a
 * guessed one — see hide().
 */
(function () {
  if (window.__wattecFpsInit) return;
  window.__wattecFpsInit = true;

  /* ---------------------------------------------------------------------
     Graphics cards, scored against an RTX 5090 at 100.
     Keys are matched against the product's own wording, longest key first,
     so "RTX 5070 Ti" is never mistaken for "RTX 5070".
     --------------------------------------------------------------------- */
  var GPUS = {
    'rtx 5090': 100,
    'rtx 5080 super': 78,
    'rtx 5080': 71,
    'rtx 5070 ti': 60,
    'rtx 5070': 47,
    'rtx 5060 ti': 36,
    'rtx 5060': 29,
    'rtx 5050': 22,

    'rtx 4090': 87,
    'rtx 4080 super': 70,
    'rtx 4080': 68,
    'rtx 4070 ti super': 60,
    'rtx 4070 ti': 55,
    'rtx 4070 super': 51,
    'rtx 4070': 43,
    'rtx 4060 ti': 32,
    'rtx 4060': 26,

    'rtx 3090 ti': 58,
    'rtx 3090': 55,
    'rtx 3080 ti': 55,
    'rtx 3080': 51,
    'rtx 3070 ti': 39,
    'rtx 3070': 36,
    'rtx 3060 ti': 32,
    'rtx 3060': 24,
    'rtx 3050': 15,

    'rtx 2080 ti': 36,
    'rtx 2080 super': 30,
    'rtx 2080': 28,
    'rtx 2070 super': 29,
    'rtx 2070': 25,
    'rtx 2060 super': 24,
    'rtx 2060': 21,

    'gtx 1080 ti': 27,
    'gtx 1080': 21,
    'gtx 1070 ti': 19,
    'gtx 1070': 17,
    'gtx 1660 super': 16,
    'gtx 1660 ti': 16,
    'gtx 1660': 14,
    'gtx 1650 super': 12,
    'gtx 1650': 9,
    'gtx 1060': 11,

    'rx 9070 xt': 62,
    'rx 9070 gre': 46,
    'rx 9070': 53,
    'rx 9060 xt': 31,
    'rx 9060': 26,
    'rx 7900 xtx': 68,
    'rx 7900 xt': 58,
    'rx 7900 gre': 47,
    'rx 7800 xt': 44,
    'rx 7700 xt': 36,
    'rx 7600 xt': 25,
    'rx 7600': 24,
    'rx 6950 xt': 50,
    'rx 6900 xt': 47,
    'rx 6800 xt': 45,
    'rx 6800': 39,
    'rx 6750 xt': 32,
    'rx 6700 xt': 30,
    'rx 6650 xt': 24,
    'rx 6600 xt': 23,
    'rx 6600': 21,
    'rx 6500 xt': 11,
    'rx 5700 xt': 22,
    'rx 5600 xt': 18,
    'rx 580': 11,

    'arc b580': 26,
    'arc b570': 22,
    'arc a770': 22,
    'arc a750': 20
  };

  /* ---------------------------------------------------------------------
     Processors, scored against a Ryzen 7 9800X3D at 100. Matched on the model
     number alone, which is the part of the name the store writes consistently
     ("Ryzen 7 9800X3D", "Intel i9 14900K", "AMD Ryzen 5 9600X").
     --------------------------------------------------------------------- */
  /* Keys are matched as whole words, so a suffix is a different part:
     "245kf" is not found under "245k", and each needs its own line. */
  var CPUS = {
    '9950x3d2': 103,
    '9950x3d': 102,
    '9900x3d': 99,
    '9850x3d': 103,
    '9800x3d': 100,
    '9950x': 90,
    '9900x': 86,
    '9700x': 82,
    '9600x': 78,
    '9600': 76,
    '7950x3d': 89,
    '7900x3d': 86,
    '7800x3d': 88,
    '7600x3d': 82,
    '7500x3d': 80,
    '7950x': 82,
    '7900x': 79,
    '7900': 77,
    '7700x': 78,
    '7700': 76,
    '7600x': 75,
    '7600': 72,
    '7500f': 71,
    '8700g': 69,
    '8600g': 66,
    '8500g': 60,
    '8400f': 64,
    '5800x3d': 69,
    '5700x3d': 62,
    '5950x': 60,
    // Written with a space: normalise() splits an "XT" suffix off any model
    // number, which is meant for graphics cards but catches this one too.
    '5900 xt': 59,
    '5900x': 59,
    '5800x': 56,
    '5700x': 54,
    '5700g': 50,
    '5600x': 55,
    '5600gt': 48,
    '5600g': 47,
    '5600': 54,
    '5500': 47,
    '4500': 42,
    '4100': 38,
    '3700x': 44,
    '3600x': 43,
    '3600': 42,
    '2700x': 36,
    '2600x': 34,
    '2600': 33,

    '285kf': 93,
    '285k': 93,
    '270k': 91,
    '265kf': 89,
    '265k': 89,
    '250kf': 86,
    '250k': 86,
    '245kf': 82,
    '245k': 82,
    '225f': 74,
    '225': 74,
    '14900ks': 94,
    '14900kf': 93,
    '14900k': 93,
    '14700kf': 89,
    '14700k': 89,
    '14700f': 86,
    '14700': 86,
    '14600kf': 81,
    '14600k': 81,
    '14500': 74,
    '14400f': 68,
    '14100f': 54,
    '14100': 54,
    '13900kf': 91,
    '13900k': 91,
    '13700kf': 87,
    '13700k': 87,
    '13700': 83,
    '13600kf': 80,
    '13600k': 80,
    '13400f': 67,
    '13100f': 52,
    '12900kf': 82,
    '12900k': 82,
    '12700kf': 75,
    '12700k': 75,
    '12700f': 73,
    '12700': 73,
    '12600kf': 71,
    '12600k': 71,
    '12400f': 61,
    '12400': 61,
    '12100f': 50,
    '12100': 50,
    'g6900': 25,
    '11700k': 56,
    '11400f': 49,
    '10900k': 57,
    '10700k': 53,
    '10400f': 45,
    '9700k': 46,
    '9600k': 41,

    /* Laptop processors, which the shop sells among its rebuilt machines. They
       share a naming scheme with the desktop parts but run a long way behind
       them, so they are listed rather than left to the fallback score. */
    '285hx': 85,
    '14900hx': 82,
    '13980hx': 80,
    '13950hx': 80,
    '14650hx': 78,
    '13900h': 68,
    '13700h': 66,
    '13620h': 66,
    '12800h': 63,
    '12700h': 62,
    '11800h': 52,
    '11400h': 45,
    '10750h': 44,
    '9955hx3d': 95,
    '8845hs': 64,
    '7945hx': 79,
    '7840hs': 62,
    '6800h': 55,
    '5800h': 50,
    '4800h': 44
  };

  /* A laptop graphics card carries the same name as the desktop card it is
     named after and does not perform like it — less power, less cooling. The
     shop's rebuilt laptops would otherwise be quoted desktop frame rates. */
  var LAPTOP_GPU_FACTOR = 0.72;
  var MOBILE_CPU = /\b\d{4,5}(hx3d|hx|hs|h|u)\b/;

  /* g: [1080p, 1440p, 2160p] on the reference graphics card with no processor
     limit. c: the ceiling the reference processor places on that game.
     hard: a limit the game itself imposes whatever the hardware — GTA V's
     physics are tied to the frame rate and the engine refuses to go past
     187 fps, so a faster processor cannot lift it.
     ram: the share of frames lost with 16GB and with 8GB of memory, where
     32GB (the reference) loses none. The big open-world games stream more
     than 16GB holds and lose the most; Counter-Strike and GTA V fit easily.
     poster: the game's cover in the theme's assets, as
     wattec-game-<poster>.jpg. The panel lists the addresses of those files
     (snippets/wattec-fps.liquid), so a game without one simply has no cover. */
  var GAMES = [
    { name: 'Counter-Strike 2', g: [1100, 620, 400], c: 700, ram: [0, 0.05], poster: 'counter-strike-2' },
    { name: 'Cyberpunk 2077', g: [230, 165, 95], c: 200, ram: [0.04, 0.2], poster: 'cyberpunk-2077' },
    { name: 'Red Dead Redemption 2', g: [215, 165, 100], c: 190, ram: [0.02, 0.15], poster: 'red-dead-redemption-2' },
    { name: 'GTA V', g: [400, 300, 170], c: 187, hard: 187, ram: [0, 0.05], poster: 'gta-v' },
    { name: 'Fortnite', g: [310, 230, 140], c: 270, ram: [0.02, 0.12], poster: 'fortnite' },
    { name: 'Hogwarts Legacy', g: [205, 155, 95], c: 175, ram: [0.1, 0.35], poster: 'hogwarts-legacy' },
    { name: 'Battlefield 6', g: [290, 200, 120], c: 250, ram: [0.06, 0.25], poster: 'battlefield-6' },
    { name: 'Forza Horizon 6', g: [280, 210, 150], c: 235, ram: [0.04, 0.2], poster: 'forza-horizon-6' },
    { name: 'Black Myth: Wukong', g: [165, 125, 72], c: 185, ram: [0.04, 0.2], chartOnly: true, poster: 'black-myth-wukong' },
    { name: 'Marvel’s Spider-Man 2', g: [210, 165, 100], c: 165, ram: [0.1, 0.35], chartOnly: true, poster: 'spider-man-2' }
  ];

  /* One memory stick runs in single channel, with half the bandwidth of a
     pair. The graphics card does not notice; the processor does, so it is
     the processor's ceiling that drops. */
  var SINGLE_CHANNEL = 0.85;

  /* The two newest games are drawn on the chart but left out of the summary
     below, so adding them did not quietly lower the "4K Gaming"-style tags
     that every machine already carries. Both are heavier than the typical
     game, which is exactly why they are worth showing, and exactly why
     letting them pull the median down would change those tags. */
  var SUMMARY_GAMES = GAMES.filter(function (game) { return !game.chartOnly; });

  var RESOLUTIONS = [
    { key: '1080', label: '1080p' },
    { key: '1440', label: '1440p' },
    { key: '2160', label: '4K' }
  ];

  /* ------------------------------------------------------------------ */

  /**
   * Flattens the store's own wording into something the tables can be looked
   * up with. Model numbers are written every which way — "RX 9070XT",
   * "RTX 5060ti", "i9-14900K" — so the digits are split from the suffix that
   * follows them and the punctuation between words is levelled out.
   */
  function normalise(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\b(rtx|gtx|rx|arc)\s*(\d)/g, '$1 $2')
      .replace(/(\d{3,4})\s*(ti|xtx|xt|super|gre)\b/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Longest keys first, so a more specific card always wins its match. */
  function sortedKeys(table) {
    return Object.keys(table).sort(function (a, b) {
      return b.length - a.length;
    });
  }

  var GPU_KEYS = sortedKeys(GPUS);
  var CPU_KEYS = sortedKeys(CPUS);

  function lookup(table, keys, text) {
    var normal = normalise(text);
    if (!normal) return null;
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      // Anchored at both ends so "rx 6600" cannot match inside "rx 6600 xt",
      // which the ordering has already ruled out, nor inside a memory size.
      var pattern = new RegExp('(^|\\s)' + key.replace(/ /g, '\\s') + '($|\\s)');
      if (pattern.test(normal)) {
        return { key: key, score: table[key] };
      }
    }
    return null;
  }

  /**
   * How sharply the slower part takes over. A plain minimum (an infinite
   * value here) says a faster processor adds nothing until it is the slower
   * part, which is not how games behave: the frame-to-frame load moves
   * between the two, so the processor still counts a little when the
   * graphics card is the limit, and most where the two are close. 4 keeps
   * every machine's resolution tags where the plain minimum put them while
   * letting a processor upgrade show — most in Counter-Strike and GTA V.
   */
  var BOTTLENECK = 4;

  /**
   * What the memory text says, as { gb, single }. The store writes it as
   * "16GB DDR4", "Corsair Vengeance 32GB 6000MHz", or, in the build-your-own
   * lists, "Team T Force Vulcan Z 16GB Kit 2 x 8GB CL18" for a pair and
   * "Kingston Fury Beast 8GB" for a single stick. single is true or false
   * only where the text says so ("2 x 8GB", "1x16GB"), otherwise null.
   */
  function readRam(text) {
    var normal = String(text || '').toLowerCase();
    var kit = normal.match(/\b(\d)\s*x\s*(\d+)\s*gb/);
    if (kit) return { gb: kit[1] * kit[2], single: kit[1] === '1' };
    var size = normal.match(/\b(\d+)\s*gb/);
    if (!size) return null;
    return { gb: Number(size[1]), single: /single/.test(normal) ? true : null };
  }

  /** "16GB RAM", said beside the parts in the chart's heading. */
  function ramLabel(ram) {
    if (!ram) return '';
    return ram.gb + 'GB RAM' + (ram.single ? ' (single stick)' : '');
  }

  function estimate(game, resolutionIndex, gpuScore, cpuScore, ram) {
    var byGpu = game.g[resolutionIndex] * (gpuScore / 100);
    var byCpu = game.c * (cpuScore / 100) * (ram && ram.single ? SINGLE_CHANNEL : 1);
    var fps = Math.pow(Math.pow(byGpu, -BOTTLENECK) + Math.pow(byCpu, -BOTTLENECK), -1 / BOTTLENECK);
    if (game.hard) fps = Math.min(fps, game.hard);
    // Unknown memory is left out rather than guessed at, and anything from
    // 24GB up runs these games as well as the 32GB reference does.
    if (ram && ram.gb < 24) fps *= 1 - game.ram[ram.gb >= 12 ? 0 : 1];
    return Math.max(1, Math.round(fps));
  }

  /** A round number just above the tallest bar, so the axis reads sensibly. */
  function axisMax(highest) {
    var step = highest > 400 ? 100 : highest > 150 ? 50 : 20;
    return Math.max(step, Math.ceil(highest / step) * step);
  }

  function buildChart(chart, gpuScore, cpuScore, ram, posters) {
    posters = posters || {};
    var rows = GAMES.map(function (game) {
      return {
        name: game.name,
        poster: posters[game.poster] || '',
        values: RESOLUTIONS.map(function (resolution, index) {
          return estimate(game, index, gpuScore, cpuScore, ram);
        })
      };
    });

    var highest = 0;
    rows.forEach(function (row) {
      row.values.forEach(function (value) {
        if (value > highest) highest = value;
      });
    });

    var max = axisMax(highest);
    chart.textContent = '';

    // With covers, every row is indented by the cover column, and the 60 FPS
    // line has to move over by the same amount to stay on the bars.
    var withPosters = rows.some(function (row) { return row.poster; });
    chart.classList.toggle('wt-fps__chart--posters', withPosters);

    // The 60 FPS line, the figure most people judge "smooth" by. It sits in
    // its own layer over the bars, inset by the width of the number column so
    // it lines up with the tracks rather than the whole panel.
    if (max >= 120) {
      var layer = document.createElement('div');
      layer.className = 'wt-fps__mark-layer';
      var mark = document.createElement('span');
      mark.className = 'wt-fps__mark';
      mark.style.left = (60 / max) * 100 + '%';
      mark.setAttribute('data-label', '60 FPS');
      layer.appendChild(mark);
      chart.appendChild(layer);
    }

    rows.forEach(function (row) {
      var game = document.createElement('div');
      game.className = 'wt-fps__game';

      // The cover is decoration — the game's name is written beside it — so
      // screen readers are told to pass over it. A row without a cover keeps
      // an empty tile in its place, so the bars still line up.
      if (withPosters) {
        var cover = document.createElement('span');
        cover.className = 'wt-fps__poster';
        if (row.poster) {
          var img = document.createElement('img');
          img.src = row.poster;
          img.alt = '';
          img.width = 120;
          img.height = 180;
          img.loading = 'lazy';
          img.decoding = 'async';
          cover.appendChild(img);
        }
        game.appendChild(cover);
      }

      var body = document.createElement('div');
      body.className = 'wt-fps__game-body';
      game.appendChild(body);

      var name = document.createElement('p');
      name.className = 'wt-fps__game-name';
      name.textContent = row.name;
      body.appendChild(name);

      var bars = document.createElement('div');
      bars.className = 'wt-fps__bars';

      row.values.forEach(function (value, index) {
        var resolution = RESOLUTIONS[index];

        var bar = document.createElement('div');
        bar.className = 'wt-fps__bar wt-fps__bar--' + resolution.key;

        var track = document.createElement('span');
        track.className = 'wt-fps__track';

        var fill = document.createElement('span');
        fill.className = 'wt-fps__fill';
        // Kept off zero so the slowest combinations still read as a bar
        // rather than as an empty row.
        fill.dataset.width = Math.max(2.5, (value / max) * 100) + '%';
        track.appendChild(fill);

        var figure = document.createElement('span');
        figure.className = 'wt-fps__value';
        figure.textContent = value;

        // Said in full for screen readers, which would otherwise read the
        // three bars of a game as three bare numbers.
        bar.setAttribute('role', 'img');
        bar.setAttribute(
          'aria-label',
          row.name + ' at ' + resolution.label + ': about ' + value + ' frames per second'
        );

        bar.appendChild(track);
        bar.appendChild(figure);
        bars.appendChild(bar);
      });

      body.appendChild(bars);
      chart.appendChild(game);
    });

    return chart;
  }

  /**
   * Grows the bars once the chart is actually on screen. On a panel this far
   * down the page the movement is what makes the comparison read; where the
   * visitor has asked for less motion, or the browser cannot tell us when the
   * panel appears, the bars are simply drawn at their full length.
   */
  function reveal(root) {
    var fills = root.querySelectorAll('.wt-fps__fill');

    function paint() {
      fills.forEach(function (fill) {
        fill.style.width = fill.dataset.width;
      });
    }

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      paint();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          window.requestAnimationFrame(paint);
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(root);
  }

  /**
   * Whether the panel and its collapsible row are on the page at all.
   *
   * Both are delivered switched off by the stylesheet and only turned on once
   * a graphics card has actually been recognised, so a product this cannot
   * read — a keyboard, or a machine part-way through being configured — never
   * shows an empty chart or leaves an empty row for a customer to open. It is
   * a class rather than a removal because on the custom-build pages the
   * answer changes as the customer picks parts, and the panel has to be able
   * to come back.
   */
  function setAvailable(available) {
    document.body.classList.toggle('wt-fps-ready', !!available);
  }

  /**
   * What the machine is good for, in words, worked out from the very same
   * estimates the chart draws — so the "4K Gaming" tag on the image, in the
   * floating bar and on the "Who it's for" cards can never promise more than
   * the chart shows. The typical game (the median of the original eight) is used rather
   * than the average, which Counter-Strike's huge figures would drag upwards.
   *
   * Published as window.wattecGaming and as a "wattec:gaming" event on the
   * document; null when there is no graphics card to judge by.
   */
  function median(values) {
    var sorted = values.slice().sort(function (a, b) { return a - b; });
    var mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }

  function summarise(gpuScore, cpuScore, ram, parts) {
    var typical = RESOLUTIONS.map(function (resolution, index) {
      return Math.round(median(SUMMARY_GAMES.map(function (game) {
        return estimate(game, index, gpuScore, cpuScore, ram);
      })));
    });
    return {
      esports1080: Math.min(estimate(GAMES[0], 0, gpuScore, cpuScore, ram), estimate(GAMES[4], 0, gpuScore, cpuScore, ram)),
      fps1080: typical[0],
      fps1440: typical[1],
      fps2160: typical[2],
      cpuScore: cpuScore,
      gpu: parts.gpu,
      cpu: parts.cpu
    };
  }

  /**
   * The same summary for any other machine — used by the "Recommended
   * systems" cards, which know each PC's processor, graphics card and memory
   * but have no chart of their own. Null when the graphics card is not
   * recognised.
   */
  window.wattecGamingFor = function (cpuText, gpuText, ramText) {
    var gpu = lookup(GPUS, GPU_KEYS, gpuText);
    if (!gpu) return null;
    var cpu = lookup(CPUS, CPU_KEYS, cpuText);
    var cpuScore = cpu ? cpu.score : 70;
    var gpuScore = MOBILE_CPU.test(normalise(cpuText)) ? gpu.score * LAPTOP_GPU_FACTOR : gpu.score;
    return summarise(gpuScore, cpuScore, readRam(ramText), { cpu: cpuText, gpu: gpuText });
  };
  document.dispatchEvent(new CustomEvent('wattec:gaming-ready'));

  function publishGaming(gpuScore, cpuScore, ram, parts) {
    // The competitive shooters people buy high-refresh monitors for are
    // judged by the slower of Counter-Strike 2 and Fortnite at 1080p.
    var summary = gpuScore ? summarise(gpuScore, cpuScore, ram, parts) : null;
    var key = JSON.stringify(summary);
    if (key === publishGaming.last) return;
    publishGaming.last = key;
    window.wattecGaming = summary;
    document.dispatchEvent(new CustomEvent('wattec:gaming', { detail: summary }));
  }

  /* ---------------------------------------------------------------------
     Parts chosen in the options app.

     On the build-your-own machines there is no processor or graphics card to
     read from the product itself — the customer picks them from the Easify
     option list, and the chart follows along. Each of those lists writes the
     chosen part into a hidden field named after the list it belongs to
     ("AM5 CPUs", "Nvidia Graphics Cards"), which is what is read here.

     Lists the customer cannot currently see are skipped: switching platform
     leaves the old platform's list filled in but hidden, and its part is no
     longer the one being bought.
     --------------------------------------------------------------------- */

  // Names that contain the word "GPU" or "CPU" without being a choice of one:
  // this store has a "RGB Cables GPU & Motherboard" list, and cooler lists
  // that name the processor they fit.
  var NOT_A_PART = /cable|cooler|fan|paste|thermal|monitor|keyboard|mouse|headphone|chair|desk|pad|warranty|service/;

  /** Easify appends its own price to the chosen value, e.g. "… [ +£399.00 ]". */
  function cleanOptionValue(value) {
    return String(value || '')
      .replace(/[\[(][^\])]*[\])]/g, ' ')
      .trim();
  }

  function easifyParts() {
    var container = document.querySelector('.tpo_option-set-container');
    if (!container) return null;

    var found = { cpu: '', gpu: '', ram: null };
    var anyCpu = '';

    container.querySelectorAll('.tpo_option-container').forEach(function (group) {
      if (group.classList.contains('tpo_hidden')) return;
      if (window.getComputedStyle(group).display === 'none') return;

      var input = group.querySelector('input[type="text"][name^="properties["]');
      if (!input || !input.value) return;

      var match = input.name.match(/^properties\[(.*)\]$/);
      var name = (match ? match[1] : '').toLowerCase();
      if (NOT_A_PART.test(name)) return;

      var value = cleanOptionValue(input.value);
      // A list whose name mentions graphics or processors is not always the
      // part itself: the custom builds open with "Choose Your Graphic Card
      // Option", whose answer is just a brand ("Nvidia"). The first choice
      // that names a card or processor the tables recognise wins.
      if (!found.gpu && /graphic/.test(name) && lookup(GPUS, GPU_KEYS, value)) found.gpu = value;
      if (/\bcpus?\b|processor/.test(name)) {
        if (!found.cpu && lookup(CPUS, CPU_KEYS, value)) found.cpu = value;
        if (!anyCpu) anyCpu = value;
      }
      // The memory lists ("DDR4 MEMORY", "DDR5 Memory") name every pair as a
      // kit, so a stick listed without one is sold on its own. The pre-built
      // machines' "Upgrade RAM" choice ("48 GB DDR5") says nothing either
      // way and is left as unknown.
      if (!found.ram && /\bram\b|memory/.test(name) && !/motherboard/.test(name)) {
        var ram = readRam(value);
        if (ram && ram.single === null && /memory/.test(name) && !/upgrade/.test(name)) ram.single = true;
        found.ram = ram;
      }
    });

    // A processor the tables do not know is still named in the chart's
    // heading; the estimate then uses a mid-range processor score.
    if (!found.cpu) found.cpu = anyCpu;
    return found;
  }

  function specsForVariant(variantId) {
    var el = document.querySelector('.variant-specs-data');
    if (!el) return null;
    try {
      var map = JSON.parse(el.textContent);
      return map[variantId] || null;
    } catch (e) {
      return null;
    }
  }

  // What the product itself says, and what the customer has picked in the
  // options app. The options app wins where it has an answer, because on
  // those products it is the customer's own choice being priced.
  var product = { cpu: '', gpu: '', ram: '' };
  var laptop = false;
  var drawn = null;

  function currentParts() {
    var picked = easifyParts();
    // Tidied because these come from whatever was typed into the product's own
    // fields, which sometimes carries a stray space at either end and shows up
    // as a gap in the sentence naming the parts.
    var tidy = function (value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    };
    return {
      cpu: tidy((picked && picked.cpu) || product.cpu),
      gpu: tidy((picked && picked.gpu) || product.gpu),
      ram: (picked && picked.ram) || readRam(product.ram)
    };
  }

  /** The covers' addresses, written into the panel by the theme. */
  function readPosters(root) {
    try {
      return JSON.parse(root.dataset.posters || '{}');
    } catch (error) {
      return {};
    }
  }

  function render(force) {
    var panels = document.querySelectorAll('[data-wt-fps]');
    if (!panels.length) return;

    var parts = currentParts();
    var key = JSON.stringify(parts);
    if (!force && key === drawn) return;
    drawn = key;

    var gpu = lookup(GPUS, GPU_KEYS, parts.gpu);
    var cpu = lookup(CPUS, CPU_KEYS, parts.cpu);

    // Without a graphics card there is nothing to base an estimate on. An
    // unrecognised processor is survivable: a mid-range score keeps the
    // graphics figures honest at 1440p and 4K, where the processor barely
    // matters anyway.
    if (!gpu) {
      setAvailable(false);
      publishGaming(null);
      return;
    }
    var cpuScore = cpu ? cpu.score : 70;

    // Either the product says it is a laptop, or its processor's model number
    // ends in one of the suffixes only laptop parts carry.
    var isLaptop = laptop || MOBILE_CPU.test(normalise(parts.cpu));
    var gpuScore = isLaptop ? gpu.score * LAPTOP_GPU_FACTOR : gpu.score;

    panels.forEach(function (root) {
      var chart = root.querySelector('[data-fps-chart]');
      if (!chart) return;

      buildChart(chart, gpuScore, cpuScore, parts.ram, readPosters(root));

      // Named in full so the customer can see the chart is about the machine
      // in front of them, and — on a build-your-own page — that it moved when
      // they changed a part.
      var line = root.querySelector('[data-fps-parts]');
      if (line) {
        var named = [parts.cpu, ramLabel(parts.ram)].filter(Boolean);
        line.textContent = 'Estimated for ' + parts.gpu +
          (named.length ? ' with ' + named.join(' and ') : '');
      }

      root.hidden = false;
      reveal(root);
    });

    setAvailable(true);
    publishGaming(gpuScore, cpuScore, parts.ram, parts);
  }

  function applyVariant(variantId) {
    var specs = specsForVariant(variantId);
    if (!specs) return;
    // An empty value means the product has no option for that part, so the
    // figure written into the page from its metafield is the right one and
    // must be left alone.
    if (specs.cpu) product.cpu = specs.cpu;
    if (specs.gpu) product.gpu = specs.gpu;
    if (specs.ram) product.ram = specs.ram + ' ' + (specs.table_ram || '');
    render();
  }

  /**
   * Follows the options app. Easify redraws whole option lists as the
   * customer works through a build, so a watcher on the container plus a slow
   * poll covers both the changes it announces and the ones it does not.
   * render() does nothing unless the parts have actually changed, so the poll
   * costs a string comparison.
   *
   * Reading which parts are chosen still means checking every list's style,
   * and Easify changes its markup several times for a single tap, so the
   * watcher waits for the changes to settle and reads once, rather than
   * reading after every one of them.
   */
  function watchOptions() {
    var container = document.querySelector('.tpo_option-set-container');
    if (!container) {
      // The app may not have drawn its options yet on a build-your-own page.
      setTimeout(watchOptions, 800);
      return;
    }

    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        render();
      }, 120);
    }).observe(container, { childList: true, subtree: true, attributes: true, characterData: true });

    container.addEventListener('change', function () {
      setTimeout(render, 60);
    });

    setInterval(render, 900);
    render();
  }

  function init() {
    var first = document.querySelector('[data-wt-fps]');
    if (!first) return;

    product.cpu = first.dataset.cpu || '';
    product.gpu = first.dataset.gpu || '';
    product.ram = first.dataset.ram || '';
    laptop = first.dataset.laptop === 'true';
    render(true);
    watchOptions();
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (target && target.name === 'id' && target.value) {
      applyVariant(target.value);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
